import React from "react";
import Modal from "../UI/modal";
import { Payment } from "../../types";
import moment from "moment";

interface PendingPaymentsModalProps {
  open: boolean;
  onClose: () => void;
  payments: Payment[];
  isLoading?: boolean;
}

const PendingPaymentsModal: React.FC<PendingPaymentsModalProps> = ({
  open,
  onClose,
  payments,
  isLoading = false,
}) => {
  const pendingPayments =
    payments?.filter(
      (payment) =>
        payment.status === "pending" ||
        payment.service?.payment_status === "pending"
    ) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Modal open={open} close={onClose} maxWidth="sm">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">Pending Payments</h3>
          {/* <button
            className="text-gray-500 hover:text-gray-700 transition-colors"
            onClick={onClose}
          >
            ✕
          </button> */}
        </div>

        {/* Loader */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : pendingPayments.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto h-20 w-20 text-gray-300 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-3.313 0-6 2.687-6 6s2.687 6 6 6 6-2.687 6-6-2.687-6-6-6z"
              />
            </svg>
            <h5 className="text-gray-500 text-lg font-medium">
              No pending payments
            </h5>
            <p className="text-gray-400 mt-2">
              All your payments are up to date.
            </p>
          </div>
        ) : (
          /* Payment List */
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {pendingPayments.map((payment) => (
              <div
                key={payment.id}
                className="bg-white border border-gray-200 rounded-lg p-5 shadow hover:shadow-lg transition-shadow flex flex-col md:flex-row justify-between gap-4"
              >
                {/* Payment Info */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3 h-3 rounded-full animate-pulse ${getStatusColor(
                        payment.status
                      )}`}
                    ></span>
                    <h6 className="text-gray-800 font-semibold text-lg">
                      {payment.service?.title?.length > 30
                        ? `${payment.service?.title?.slice(0, 30)}...`
                        : payment.service?.title || "Payment"}
                    </h6>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                    <div className="space-y-1">
                      <p>
                        <span className="font-medium">Case Number:</span>{" "}
                        {payment.service?.case_number || "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Suit Number:</span>{" "}
                        {payment.service?.suit_number || "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Court:</span>{" "}
                        {payment.service?.court || "N/A"}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <p>
                        <span className="font-medium">Amount:</span>{" "}
                        <span className="font-bold text-green-600">
                          ₦{payment.amount?.toLocaleString() || "0"}
                        </span>
                      </p>
                      <p>
                        <span className="font-medium">Fee:</span>{" "}
                        {payment.fee
                          ? `₦${payment.fee.toLocaleString()}`
                          : "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Created:</span>{" "}
                        {moment(payment.created_at).format("MMM DD, YYYY")}
                      </p>
                    </div>
                  </div>

                  {payment.comment && (
                    <div className="mt-2 p-2 bg-gray-50 rounded">
                      <p className="text-gray-600 text-sm">
                        <span className="font-medium">Note:</span>{" "}
                        {payment.comment}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action */}
                <div className="flex flex-col items-start md:items-end justify-between gap-3">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      payment.status
                    )}`}
                  >
                    {payment.status.charAt(0).toUpperCase() +
                      payment.status.slice(1)}
                  </span>

                  <button
                    onClick={() => (window.location.href = payment.url)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full font-medium transition-transform hover:scale-105 flex items-center gap-2"
                  >
                    Pay Now
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {pendingPayments.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-gray-600 font-medium">
              Total Pending: {pendingPayments.length} payment
              {pendingPayments.length !== 1 ? "s" : ""}
            </p>
            <h6 className="font-bold text-green-600 text-lg">
              ₦
              {pendingPayments
                .reduce((sum, payment) => sum + (payment.amount || 0), 0)
                .toLocaleString()}
            </h6>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PendingPaymentsModal;
